import { FileCheck2, Handshake, ShieldCheck } from "lucide-react";
import InsurerPartnerLogo from "@/components/InsurerPartnerLogo";
import Booking from "./Booking";
import ServicePageFaqSection from "./ServicePageFaqSection";
import { useFaqPageJsonLd } from "@/hooks/useFaqPageJsonLd";

export default function ClaimsPage({ t, lang = "ro" }) {
  useFaqPageJsonLd(t.claimsPage.faq, "service-claims-faq");

  return (
    <article
      id="claims-page"
      data-testid="claims-page"
      className="bg-white border-b border-[#e2e6ec]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">

        <header>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#e3191b] mb-4">
            {t.claimsPage.eyebrow}
          </div>
          <h1 className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            {t.claimsPage.title}
          </h1>
          {t.claimsPage.tagline ? (
            <p className="mt-5 max-w-3xl text-lg font-semibold text-[#0b1220] leading-snug">
              {t.claimsPage.tagline}
            </p>
          ) : null}
          <p className="mt-6 max-w-3xl text-slate-600 leading-relaxed text-base sm:text-lg">
            {t.claimsPage.lead}
          </p>
          {t.claimsPage.secondary ? (
            <p className="mt-5 max-w-3xl text-slate-600 leading-relaxed text-base sm:text-lg">
              {t.claimsPage.secondary}
            </p>
          ) : null}
        </header>

        <section className="mt-12" aria-labelledby="claims-cards-heading">
          <h2
            id="claims-cards-heading"
            className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-2xl sm:text-3xl mb-8 max-w-4xl leading-tight"
          >
            {t.claimsPage.cardsSectionTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <FileCheck2 className="w-4 h-4" />
                {t.claimsPage.card1Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.claimsPage.card1Text}</p>
            </div>
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <Handshake className="w-4 h-4" />
                {t.claimsPage.card2Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.claimsPage.card2Text}</p>
            </div>
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <ShieldCheck className="w-4 h-4" />
                {t.claimsPage.card3Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.claimsPage.card3Text}</p>
            </div>
          </div>
        </section>

        <div className="mt-8 border border-[#e2e6ec] bg-[#f7f8fa] p-6">
          <h2 className="font-[Barlow_Condensed] font-bold uppercase text-[#0b1220] text-3xl tracking-tight">
            {t.claimsPage.partnersTitle}
          </h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {t.claimsPage.partners.map((partner) => (
              <a
                key={partner.url}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full shrink-0 items-center gap-2 border border-[#e2e6ec] bg-white text-slate-700 font-mono text-xs uppercase tracking-wider px-3 py-2 hover:border-[#0b378c] hover:text-[#0b378c] transition-colors"
              >
                <InsurerPartnerLogo url={partner.url} />
                <span className="min-w-0 whitespace-normal break-words leading-snug">{partner.name}</span>
                <span className="text-[#e3191b] text-[10px]" aria-hidden="true">
                  ↗
                </span>
              </a>
            ))}
          </div>
          {t.claimsPage.disclaimer ? (
            <p className="mt-5 max-w-4xl text-xs text-slate-500 leading-relaxed border-t border-[#e2e6ec] pt-5">
              {t.claimsPage.disclaimer}
            </p>
          ) : null}
        </div>

        <ServicePageFaqSection
          headingId="claims-faq-heading"
          title={t.claimsPage.faqTitle}
          faq={t.claimsPage.faq}
        />

        <div className="mt-12">
          <Booking
            t={t}
            initialService={t.services.items[5]?.title || ""}
            copyOverrides={t.claimsPage.bookingCopy}
          />
        </div>
      </div>
    </article>
  );
}
